import React, { useState, useEffect } from "react";
import {
  FaUserGraduate,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaEye,
  FaSearch,
  FaUniversity,
  FaPassport,
  FaCheckCircle,
  FaExclamationCircle,
  FaClock,
  FaKey,
  FaEnvelope,
  FaMoneyBillWave,
  FaHandshake,
  FaCopy,
} from "react-icons/fa";
import {
  getAllStudents,
  addStudent,
  updateStudent,
  deleteStudent,
  ensureQueryCode,
} from "../utils/emailUtils";
import { getStudentDocuments } from "../firebase";
import { logAdminActivity } from "../utils/adminSecurity";
import "./StudentTracker.css";

const StudentTracker = () => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // 'add', 'edit', 'view'
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [studentDocuments, setStudentDocuments] = useState(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    registrationDate: "",
    appliedSchools: [
      {
        schoolName: "",
        applicationDate: "",
        status: "",
      },
    ],
    offeredPrice: "",
    agreedPrice: "",
    installmentCount: "1",
    visaApplicationDate: "",
    visaStatus: "",
    credentials: [
      {
        platform: "UniAssist",
        email: "",
        password: "",
      },
    ],
  });

  const applicationStatusOptions = [
    { value: "", label: "Seçiniz" },
    { value: "preparing", label: "Hazırlanıyor", color: "#ed8936" },
    { value: "submitted", label: "Gönderildi", color: "#3182ce" },
    { value: "under_review", label: "İnceleniyor", color: "#805ad5" },
    { value: "accepted", label: "Kabul Edildi", color: "#38a169" },
    { value: "rejected", label: "Reddedildi", color: "#e53e3e" },
    { value: "waitlist", label: "Bekleme Listesi", color: "#d69e2e" },
  ];

  const visaStatusOptions = [
    { value: "", label: "Seçiniz" },
    { value: "not_applied", label: "Başvurulmadı", color: "#718096" },
    { value: "preparing", label: "Hazırlanıyor", color: "#ed8936" },
    { value: "submitted", label: "Gönderildi", color: "#3182ce" },
    {
      value: "interview_scheduled",
      label: "Mülakat Planlandı",
      color: "#805ad5",
    },
    { value: "approved", label: "Onaylandı", color: "#38a169" },
    { value: "rejected", label: "Reddedildi", color: "#e53e3e" },
  ];

  useEffect(() => {
    loadStudents();
  }, []);

  useEffect(() => {
    filterStudents();
  }, [students, searchTerm, statusFilter]);

  const loadStudents = async () => {
    setIsLoading(true);
    try {
      const studentData = await getAllStudents();
      setStudents(studentData);
    } catch (error) {
      console.error("Error loading students:", error);
    }
    setIsLoading(false);
  };

  const filterStudents = () => {
    let filtered = [...students];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (student) =>
          student.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (student.appliedSchools &&
            student.appliedSchools.some(
              (school) =>
                school.schoolName &&
                school.schoolName
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase())
            )) ||
          (student.appliedSchool &&
            student.appliedSchool
              .toLowerCase()
              .includes(searchTerm.toLowerCase()))
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (student) =>
          student.applicationStatus === statusFilter ||
          (student.appliedSchools &&
            student.appliedSchools.some(
              (school) => school.status === statusFilter
            ))
      );
    }

    // Sort by registration date (newest first)
    filtered.sort(
      (a, b) => new Date(b.registrationDate) - new Date(a.registrationDate)
    );

    setFilteredStudents(filtered);
  };

  const resetForm = () => {
    setFormData({
      firstName: "",
      lastName: "",
      registrationDate: "",
      appliedSchools: [
        {
          schoolName: "",
          applicationDate: "",
          status: "",
        },
      ],
      offeredPrice: "",
      agreedPrice: "",
      installmentCount: "1",
      visaApplicationDate: "",
      visaStatus: "",
      credentials: [
        {
          platform: "UniAssist",
          email: "",
          password: "",
        },
      ],
    });
  };

  const openModal = async (mode, student = null) => {
    setModalMode(mode);
    setSelectedStudent(student);

    // Load documents if viewing student details and student has ogrenciNumarasi
    if (mode === "view" && student && (student.ogrenciNumarasi || student.sorgulaKodu)) {
      setLoadingDocuments(true);
      try {
        const studentNumber = student.ogrenciNumarasi || student.sorgulaKodu;
        const docs = await getStudentDocuments(studentNumber);
        setStudentDocuments(docs);
      } catch (error) {
        console.error("Error loading documents:", error);
        setStudentDocuments(null);
      } finally {
        setLoadingDocuments(false);
      }
    } else {
      setStudentDocuments(null);
    }

    if (mode === "add") {
      resetForm();
    } else if (mode === "edit" && student) {
      setFormData({
        firstName: student.firstName || "",
        lastName: student.lastName || "",
        registrationDate: student.registrationDate || "",
        appliedSchools:
          student.appliedSchools ||
          (student.appliedSchool
            ? [
                {
                  schoolName: student.appliedSchool,
                  applicationDate: student.schoolApplicationDate || "",
                  status: student.applicationStatus || "",
                },
              ]
            : [
                {
                  schoolName: "",
                  applicationDate: "",
                  status: "",
                },
              ]),
        offeredPrice: student.offeredPrice || "",
        agreedPrice: student.agreedPrice || "",
        installmentCount: student.installmentCount || "1",
        visaApplicationDate: student.visaApplicationDate || "",
        visaStatus: student.visaStatus || "",
        credentials: student.credentials || [
          {
            platform: "UniAssist",
            email: "",
            password: "",
          },
        ],
      });
    }

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedStudent(null);
    setStudentDocuments(null);
    resetForm();
  };

  const handleViewStudent = (student) => {
    openModal("view", student);
  };

  const handleEditStudent = (student) => {
    openModal("edit", student);
  };

  const handleAddStudent = () => {
    openModal("add");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCredentialChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      credentials: prev.credentials.map((cred, i) =>
        i === index ? { ...cred, [field]: value } : cred
      ),
    }));
  };

  const handleSchoolChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      appliedSchools: prev.appliedSchools.map((school, i) =>
        i === index ? { ...school, [field]: value } : school
      ),
    }));
  };

  const addCredential = () => {
    setFormData((prev) => ({
      ...prev,
      credentials: [
        ...prev.credentials,
        {
          platform: "",
          email: "",
          password: "",
        },
      ],
    }));
  };

  const removeCredential = (index) => {
    if (formData.credentials.length > 1) {
      setFormData((prev) => ({
        ...prev,
        credentials: prev.credentials.filter((_, i) => i !== index),
      }));
    }
  };

  const addSchool = () => {
    setFormData((prev) => ({
      ...prev,
      appliedSchools: [
        ...prev.appliedSchools,
        {
          schoolName: "",
          applicationDate: "",
          status: "",
        },
      ],
    }));
  };

  const removeSchool = (index) => {
    if (formData.appliedSchools.length > 1) {
      setFormData((prev) => ({
        ...prev,
        appliedSchools: prev.appliedSchools.filter((_, i) => i !== index),
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (modalMode === "add") {
        const newStudent = await addStudent(formData);
        setStudents((prev) => [newStudent, ...prev]);

        // Log student addition
        logAdminActivity("student_data_modification", {
          action: "add",
          studentId: newStudent.id,
          studentName: `${formData.firstName} ${formData.lastName}`,
          studentEmail: formData.email,
          studentPhone: formData.phone,
          applicationStatus: formData.applicationStatus,
          timestamp: new Date().toISOString(),
        });
      } else if (modalMode === "edit" && selectedStudent) {
        await updateStudent(selectedStudent.id, formData);
        setStudents((prev) =>
          prev.map((student) =>
            student.id === selectedStudent.id
              ? { ...student, ...formData }
              : student
          )
        );

        // Log student update
        logAdminActivity("student_data_modification", {
          action: "update",
          studentId: selectedStudent.id,
          oldData: {
            name: `${selectedStudent.firstName} ${selectedStudent.lastName}`,
            email: selectedStudent.email,
            phone: selectedStudent.phone,
            applicationStatus: selectedStudent.applicationStatus,
          },
          newData: {
            name: `${formData.firstName} ${formData.lastName}`,
            email: formData.email,
            phone: formData.phone,
            applicationStatus: formData.applicationStatus,
          },
          timestamp: new Date().toISOString(),
        });
      }
      closeModal();
    } catch (error) {
      console.error("Error saving student:", error);
      alert("Öğrenci kaydedilirken hata oluştu.");
    }

    setIsLoading(false);
  };

  const handleDeleteStudent = async (studentId) => {
    if (!window.confirm("Bu öğrenciyi silmek istediğinizden emin misiniz?")) {
      return;
    }

    setIsLoading(true);
    try {
      const student = students.find((s) => s.id === studentId);

      await deleteStudent(studentId);
      setStudents((prev) => prev.filter((student) => student.id !== studentId));

      // Log student deletion
      logAdminActivity("student_deletion", {
        studentId,
        studentName: `${student?.firstName} ${student?.lastName}`,
        studentEmail: student?.email,
        studentPhone: student?.phone,
        applicationStatus: student?.applicationStatus,
        registrationDate: student?.registrationDate,
        timestamp: new Date().toISOString(),
        reason: "admin_manual_deletion",
      });
    } catch (error) {
      console.error("Error deleting student:", error);
      alert("Öğrenci silinirken hata oluştu.");
    }
    setIsLoading(false);
  };

  const handleCopyQueryCode = async (studentId, currentCode) => {
    try {
      let codeToUse = currentCode;

      // If student doesn't have a code, generate one
      if (!codeToUse) {
        codeToUse = await ensureQueryCode(studentId);
        // Update the student in state
        setStudents((prev) =>
          prev.map((student) =>
            student.id === studentId
              ? { ...student, ogrenciNumarasi: codeToUse, sorgulaKodu: codeToUse }
              : student
          )
        );
      }

      // Copy to clipboard
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(codeToUse);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = codeToUse;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      alert(`Sorgula kodu kopyalandı: ${codeToUse}`);

      // Log activity
      logAdminActivity("query_code_copied", {
        studentId,
        queryCode: codeToUse,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error copying query code:", error);
      alert("Kod kopyalanırken hata oluştu.");
    }
  };



  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("tr-TR");
  };

  const getStatusColor = (status, options) => {
    const statusOption = options.find((option) => option.value === status);
    return statusOption ? statusOption.color : "#718096";
  };

  const getStatusLabel = (status, options) => {
    const statusOption = options.find((option) => option.value === status);
    return statusOption ? statusOption.label : status || "-";
  };

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getStudentStats = () => {
    const total = students.length;
    const accepted = students.filter(
      (s) => s.applicationStatus === "accepted"
    ).length;
    const underReview = students.filter(
      (s) => s.applicationStatus === "under_review"
    ).length;
    const visaApproved = students.filter(
      (s) => s.visaStatus === "approved"
    ).length;

    return { total, accepted, underReview, visaApproved };
  };

  const stats = getStudentStats();

  return (
    <div className="student-tracker">
      <div className="tracker-header">
        <h2>
          <FaUserGraduate className="section-icon" />
          Öğrenci Takibi
        </h2>
        <p>Öğrenci bilgilerini ve başvuru süreçlerini takip edin</p>
      </div>

      {/* Stats Cards */}
      <div className="student-stats">
        <div className="stat-card">
          <div className="stat-icon total">
            <FaUserGraduate />
          </div>
          <div className="stat-content">
            <h3>{stats.total}</h3>
            <p>Toplam Öğrenci</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon accepted">
            <FaCheckCircle />
          </div>
          <div className="stat-content">
            <h3>{stats.accepted}</h3>
            <p>Kabul Edildi</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon review">
            <FaClock />
          </div>
          <div className="stat-content">
            <h3>{stats.underReview}</h3>
            <p>İnceleniyor</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon visa">
            <FaPassport />
          </div>
          <div className="stat-content">
            <h3>{stats.visaApproved}</h3>
            <p>Vize Onaylandı</p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="controls-section">
        <div className="search-filter-controls">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="İsim veya okul ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Başvuru Durumu</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Tüm Durumlar</option>
              {applicationStatusOptions.slice(1).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="action-buttons">
          <button className="add-button" onClick={handleAddStudent}>
            <FaPlus />
            Yeni Öğrenci Ekle
          </button>
        </div>
      </div>

      {/* Students List */}
      <div className="students-list">
        {isLoading ? (
          <div className="loading">Öğrenciler yükleniyor...</div>
        ) : filteredStudents.length === 0 ? (
          <div className="no-students">
            <FaExclamationCircle className="no-data-icon" />
            <p>
              {students.length === 0
                ? "Henüz öğrenci kaydı bulunmuyor"
                : "Filtrelere uygun öğrenci bulunamadı"}
            </p>
          </div>
        ) : (
          filteredStudents.map((student) => (
            <div key={student.id} className="student-card">
              <div className="student-header">
                <div className="student-name">
                  <FaUserGraduate className="student-icon" />
                  <div>
                    <h3>
                      {student.firstName} {student.lastName}
                    </h3>
                    <p>Kayıt: {formatDate(student.registrationDate)}</p>
                  </div>
                </div>
                <div className="student-actions">
                  <button
                    className="action-button view"
                    onClick={() => handleViewStudent(student)}
                    title="Öğrenci Detayları"
                  >
                    <FaEye />
                  </button>
                  <button
                    className="action-button edit"
                    onClick={() => handleEditStudent(student)}
                    title="Düzenle"
                  >
                    <FaEdit />
                  </button>
                  <button
                    className="action-button delete"
                    onClick={() => handleDeleteStudent(student.id)}
                    disabled={isLoading}
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>

              <div className="student-body">
                <div className="student-info">
                  <div className="info-item">
                    <FaUniversity className="info-icon" />
                    <div>
                      <label>Başvurulan Okullar</label>
                      <span>
                        {student.appliedSchools &&
                        student.appliedSchools.length > 0
                          ? student.appliedSchools
                              .map((school) => school.schoolName)
                              .filter((name) => name)
                              .join(", ") || "-"
                          : student.appliedSchool || "-"}
                      </span>
                    </div>
                  </div>

                  <div className="info-item">
                    <FaMoneyBillWave className="info-icon" />
                    <div>
                      <label>Teklif Edilen Fiyat</label>
                      <span>
                        {student.offeredPrice
                          ? `${student.offeredPrice} €`
                          : "-"}
                      </span>
                    </div>
                  </div>

                  <div className="info-item">
                    <FaHandshake className="info-icon" />
                    <div>
                      <label>Anlaşılan Fiyat</label>
                      <span>
                        {student.agreedPrice ? `${student.agreedPrice} €` : "-"}
                      </span>
                    </div>
                  </div>

                  <div className="info-item">
                    <FaKey className="info-icon" />
                    <div>
                      <label>Öğrenci Numarası</label>
                      <div className="query-code-container">
                        <span className="query-code" style={{ color: "#667eea", fontWeight: "700" }}>
                          {student.ogrenciNumarasi || student.sorgulaKodu || "Henüz atanmadı"}
                        </span>
                        <div className="query-code-actions">
                          <button
                            className="query-code-button copy"
                            onClick={() =>
                              handleCopyQueryCode(student.id, student.ogrenciNumarasi || student.sorgulaKodu)
                            }
                            title="Numarayı Kopyala"
                          >
                            <FaCopy />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="student-status">
                  <div className="status-item">
                    <label>Başvuru Durumu</label>
                    <span
                      className="status-badge"
                      style={{
                        backgroundColor: getStatusColor(
                          student.applicationStatus,
                          applicationStatusOptions
                        ),
                      }}
                    >
                      {getStatusLabel(
                        student.applicationStatus,
                        applicationStatusOptions
                      )}
                    </span>
                  </div>

                  <div className="status-item">
                    <label>Vize Durumu</label>
                    <span
                      className="status-badge"
                      style={{
                        backgroundColor: getStatusColor(
                          student.visaStatus,
                          visaStatusOptions
                        ),
                      }}
                    >
                      {getStatusLabel(student.visaStatus, visaStatusOptions)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Student Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {modalMode === "add" && "Yeni Öğrenci Ekle"}
                {modalMode === "edit" && "Öğrenci Bilgilerini Düzenle"}
                {modalMode === "view" && "Öğrenci Detayları"}
              </h3>
              <button className="close-button" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              {modalMode === "view" && selectedStudent ? (
                <div className="student-details">
                  <div className="detail-section">
                    <h4>Kişisel Bilgiler</h4>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Ad</label>
                        <span>{selectedStudent.firstName}</span>
                      </div>
                      <div className="detail-item">
                        <label>Soyad</label>
                        <span>{selectedStudent.lastName}</span>
                      </div>
                      <div className="detail-item">
                        <label>Kayıt Tarihi</label>
                        <span>
                          {formatDate(selectedStudent.registrationDate)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h4>Başvuru Bilgileri</h4>
                    {selectedStudent.appliedSchools &&
                    selectedStudent.appliedSchools.length > 0 ? (
                      <div className="schools-list">
                        {selectedStudent.appliedSchools.map((school, index) => (
                          <div key={index} className="school-item">
                            <h5>Okul {index + 1}</h5>
                            <div className="detail-grid">
                              <div className="detail-item">
                                <label>Okul Adı</label>
                                <span>{school.schoolName || "-"}</span>
                              </div>
                              <div className="detail-item">
                                <label>Başvuru Tarihi</label>
                                <span>
                                  {formatDate(school.applicationDate)}
                                </span>
                              </div>
                              <div className="detail-item">
                                <label>Başvuru Durumu</label>
                                <span
                                  className="status-badge"
                                  style={{
                                    backgroundColor: getStatusColor(
                                      school.status,
                                      applicationStatusOptions
                                    ),
                                  }}
                                >
                                  {getStatusLabel(
                                    school.status,
                                    applicationStatusOptions
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="detail-grid">
                        <div className="detail-item">
                          <label>Başvurulan Okul</label>
                          <span>{selectedStudent.appliedSchool || "-"}</span>
                        </div>
                        <div className="detail-item">
                          <label>Okul Başvuru Tarihi</label>
                          <span>
                            {formatDate(selectedStudent.schoolApplicationDate)}
                          </span>
                        </div>
                        <div className="detail-item">
                          <label>Başvuru Durumu</label>
                          <span
                            className="status-badge"
                            style={{
                              backgroundColor: getStatusColor(
                                selectedStudent.applicationStatus,
                                applicationStatusOptions
                              ),
                            }}
                          >
                            {getStatusLabel(
                              selectedStudent.applicationStatus,
                              applicationStatusOptions
                            )}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="detail-section">
                    <h4>Fiyat Bilgileri</h4>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Teklif Edilen Fiyat</label>
                        <span>
                          {selectedStudent.offeredPrice
                            ? `${selectedStudent.offeredPrice} €`
                            : "-"}
                        </span>
                      </div>
                      <div className="detail-item">
                        <label>Anlaşılan Fiyat</label>
                        <span>
                          {selectedStudent.agreedPrice
                            ? `${selectedStudent.agreedPrice} €`
                            : "-"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-section">
                    <h4>Vize Bilgileri</h4>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Vize Başvuru Tarihi</label>
                        <span>
                          {formatDate(selectedStudent.visaApplicationDate)}
                        </span>
                      </div>
                      <div className="detail-item">
                        <label>Vize Durumu</label>
                        <span
                          className="status-badge"
                          style={{
                            backgroundColor: getStatusColor(
                              selectedStudent.visaStatus,
                              visaStatusOptions
                            ),
                          }}
                        >
                          {getStatusLabel(
                            selectedStudent.visaStatus,
                            visaStatusOptions
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {selectedStudent.credentials &&
                    selectedStudent.credentials.length > 0 && (
                      <div className="detail-section">
                        <h4>Platform Bilgileri</h4>
                        <div className="credentials-list">
                          {selectedStudent.credentials.map((cred, index) => (
                            <div key={index} className="credential-item">
                              <div className="credential-platform">
                                <FaKey className="credential-icon" />
                                <strong>{cred.platform || "Platform"}</strong>
                              </div>
                              <div className="credential-details">
                                <div className="credential-detail">
                                  <FaEnvelope className="detail-icon" />
                                  <span>{cred.email || "-"}</span>
                                </div>
                                <div className="credential-detail">
                                  <FaKey className="detail-icon" />
                                  <span>
                                    {"*".repeat(cred.password?.length || 0) ||
                                      "-"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Student Documents Section */}
                  {(selectedStudent.ogrenciNumarasi || selectedStudent.sorgulaKodu) && (
                    <div className="detail-section">
                      <h4>Yüklenen Belgeler</h4>
                      {loadingDocuments ? (
                        <div className="documents-loading">
                          <p>Belgeler yükleniyor...</p>
                        </div>
                      ) : studentDocuments && (studentDocuments.okul.length > 0 || studentDocuments.vize.length > 0) ? (
                        <div className="documents-container">
                          {/* School Documents */}
                          {studentDocuments.okul.length > 0 && (
                            <div className="document-category">
                              <h5>Okul Belgeleri</h5>
                              <div className="document-list">
                                {studentDocuments.okul.map((doc, index) => (
                                  <div key={index} className="document-item">
                                    <span className="document-name">{doc.name}</span>
                                    <a
                                      href={doc.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="document-link"
                                    >
                                      Görüntüle
                                    </a>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Visa Documents */}
                          {studentDocuments.vize.length > 0 && (
                            <div className="document-category">
                              <h5>Vize Belgeleri</h5>
                              <div className="document-list">
                                {studentDocuments.vize.map((doc, index) => (
                                  <div key={index} className="document-item">
                                    <span className="document-name">{doc.name}</span>
                                    <a
                                      href={doc.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="document-link"
                                    >
                                      Görüntüle
                                    </a>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="no-documents">
                          <p>Henüz belge yüklenmemiş</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="studenttracker_form-sections-container">
                    <div className="studenttracker_form-section">
                      <h4>Kişisel Bilgiler</h4>
                      <div className="studenttracker_form-grid">
                        <div className="studenttracker_form-group">
                          <label>Ad *</label>
                          <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="studenttracker_form-group">
                          <label>Soyad *</label>
                          <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="studenttracker_form-group">
                          <label>Programa Kayıt Tarihi *</label>
                          <input
                            type="date"
                            name="registrationDate"
                            value={formData.registrationDate}
                            onChange={handleInputChange}
                            max={getTodayDate()}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="studenttracker_form-section">
                      <div className="schools-header">
                        <h4>Başvuru Bilgileri</h4>
                        <button
                          type="button"
                          className="add-school-button"
                          onClick={addSchool}
                        >
                          <FaPlus />
                          Okul Ekle
                        </button>
                      </div>

                      <div className="schools-form">
                        {formData.appliedSchools.map((school, index) => (
                          <div key={index} className="school-form-item">
                            <div className="school-form-header">
                              <h5>Okul {index + 1}</h5>
                              {formData.appliedSchools.length > 1 && (
                                <button
                                  type="button"
                                  className="remove-school-button"
                                  onClick={() => removeSchool(index)}
                                >
                                  <FaTrash />
                                </button>
                              )}
                            </div>
                            <div className="school-form-grid">
                              <div className="studenttracker_form-group">
                                <label>Okul Adı</label>
                                <input
                                  type="text"
                                  placeholder="Örn: TU Berlin, LMU München"
                                  value={school.schoolName}
                                  onChange={(e) =>
                                    handleSchoolChange(
                                      index,
                                      "schoolName",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                              <div className="studenttracker_form-group">
                                <label>Başvuru Tarihi</label>
                                <input
                                  type="date"
                                  value={school.applicationDate}
                                  onChange={(e) =>
                                    handleSchoolChange(
                                      index,
                                      "applicationDate",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                              <div className="studenttracker_form-group">
                                <label>Başvuru Durumu</label>
                                <select
                                  value={school.status}
                                  onChange={(e) =>
                                    handleSchoolChange(
                                      index,
                                      "status",
                                      e.target.value
                                    )
                                  }
                                >
                                  {applicationStatusOptions.map((option) => (
                                    <option
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="studenttracker_form-section">
                      <h4>Fiyat Bilgileri</h4>
                      <div className="studenttracker_form-grid">
                        <div className="studenttracker_form-group">
                          <label>Teklif Edilen Fiyat (€)</label>
                          <input
                            type="number"
                            name="offeredPrice"
                            value={formData.offeredPrice}
                            onChange={handleInputChange}
                            placeholder="Örn: 5000"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <div className="studenttracker_form-group">
                          <label>Anlaşılan Fiyat (€)</label>
                          <input
                            type="number"
                            name="agreedPrice"
                            value={formData.agreedPrice}
                            onChange={handleInputChange}
                            placeholder="Örn: 4500"
                            min="0"
                            step="0.01"
                          />
                        </div>
                        <div className="studenttracker_form-group">
                          <label>Taksit Sayısı</label>
                          <select
                            name="installmentCount"
                            value={formData.installmentCount}
                            onChange={handleInputChange}
                          >
                            <option value="1">1 Taksit (Tek Ödeme)</option>
                            <option value="2">2 Taksit</option>
                            <option value="3">3 Taksit</option>
                            <option value="4">4 Taksit</option>
                            <option value="5">5 Taksit</option>
                            <option value="6">6 Taksit</option>
                            <option value="8">8 Taksit</option>
                            <option value="10">10 Taksit</option>
                            <option value="12">12 Taksit</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="studenttracker_form-section">
                    <h4>Vize Bilgileri</h4>
                    <div className="studenttracker_form-grid">
                      <div className="studenttracker_form-group">
                        <label>Vize Başvuru Tarihi</label>
                        <input
                          type="date"
                          name="visaApplicationDate"
                          value={formData.visaApplicationDate}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="studenttracker_form-group">
                        <label>Vize Durumu</label>
                        <select
                          name="visaStatus"
                          value={formData.visaStatus}
                          onChange={handleInputChange}
                        >
                          {visaStatusOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="studenttracker_form-section">
                    <div className="credentials-header">
                      <h4>Platform Bilgileri</h4>
                      <button
                        type="button"
                        className="add-credential-button"
                        onClick={addCredential}
                      >
                        <FaPlus />
                        Platform Ekle
                      </button>
                    </div>

                    <div className="credentials-form">
                      {formData.credentials.map((credential, index) => (
                        <div key={index} className="credential-form-item">
                          <div className="credential-form-header">
                            <h5>Platform {index + 1}</h5>
                            {formData.credentials.length > 1 && (
                              <button
                                type="button"
                                className="remove-credential-button"
                                onClick={() => removeCredential(index)}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>
                          <div className="credential-form-grid">
                            <div className="studenttracker_form-group">
                              <label>Platform Adı</label>
                              <input
                                type="text"
                                placeholder="Örn: UniAssist, Hochschulstart"
                                value={credential.platform}
                                onChange={(e) =>
                                  handleCredentialChange(
                                    index,
                                    "platform",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                            <div className="studenttracker_form-group">
                              <label>E-posta</label>
                              <input
                                type="email"
                                placeholder="platform@email.com"
                                value={credential.email}
                                onChange={(e) =>
                                  handleCredentialChange(
                                    index,
                                    "email",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                            <div className="studenttracker_form-group">
                              <label>Şifre</label>
                              <input
                                type="password"
                                placeholder="Platform şifresi"
                                value={credential.password}
                                onChange={(e) =>
                                  handleCredentialChange(
                                    index,
                                    "password",
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="cancel-button"
                      onClick={closeModal}
                    >
                      <FaTimes />
                      İptal
                    </button>
                    <button
                      type="submit"
                      className="save-button"
                      disabled={isLoading}
                    >
                      <FaSave />
                      {isLoading ? "Kaydediliyor..." : "Kaydet"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentTracker;
